import React, { useState, useContext } from 'react'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { Subtract } from 'utility-types'

interface DrawerOpenContextInterface {
  isDrawerOpen: boolean
  drawerWidth: number

  onChange?(isDrawerOpen: boolean, width: number): void
}

export const DrawerOpenContext =
  React.createContext<DrawerOpenContextInterface>({
    isDrawerOpen: false,
    drawerWidth: 440,
  })

export const useDrawerOpenContext = () => {
  const context = useContext(DrawerOpenContext)

  if (context === null) {
    throw new Error('DrawerOpenContext was used outside of the Provider')
  }

  return context
}

export const DrawerOpenContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerWidth, updateDrawerWidth] = useState(440)

  const handleChange = (isADrawerOpen = false, newWidth = 440) => {
    setDrawerOpen(isADrawerOpen)
    updateDrawerWidth(newWidth)
  }

  return (
    <DrawerOpenContext.Provider
      value={{
        onChange: handleChange,
        isDrawerOpen: drawerOpen,
        drawerWidth: drawerWidth,
      }}
    >
      {children}
    </DrawerOpenContext.Provider>
  )
}

export interface WithDrawerOpenContext {
  drawerOpenContext: DrawerOpenContextInterface
}

export const withDrawerOpenContext = <P extends WithDrawerOpenContext>(
  WrappedComponent: React.ComponentType<P>,
) => {
  const C = (props: Subtract<P, WithDrawerOpenContext>) => {
    return (
      <DrawerOpenContext.Consumer>
        {(value) => {
          return (
            <WrappedComponent {...(props as P)} drawerOpenContext={value} />
          )
        }}
      </DrawerOpenContext.Consumer>
    )
  }

  return hoistNonReactStatics(C, WrappedComponent)
}
